/* eslint-disable no-param-reassign */
import Vue from 'vue';
import { logger } from '../logger';

export const topStickAfter = Vue.directive('topStickAfter', {
    inserted(el: HTMLElement, binding: any) {
        updateStickyPosition(el, unpackArguments(binding?.value));
    },

    componentUpdated(el: HTMLElement, binding: any) {
        updateStickyPosition(el, unpackArguments(binding?.value));
    },
});

function updateStickyPosition(el: HTMLElement, { offset, disabled, zIndex }: UnpackedArguments) {
    if (disabled || isElementHidden(el)) {
        return;
    }

    let currentElement = resolvePreviousElementSibling(el);

    while (true) {
        if (currentElement == null) {
            logger.info('stickyAfter: no eligible element found, defaulting to static position');
            setStickyStyles(el, `${offset}px`, zIndex);
            return;
        }

        const position = window.getComputedStyle(currentElement).position;
        const top = window.getComputedStyle(currentElement).top;
        const elementHeight = currentElement.clientHeight;
        const isHidden = isElementHidden(currentElement);

        if (!isHidden && position === 'sticky' && top != null) {
            setStickyStyles(el, resolvePositionTop(top, offset + elementHeight), zIndex);
            return;
        } else {
            currentElement = resolvePreviousElementSibling(currentElement);
        }
    }
}

function setStickyStyles(el: HTMLElement, top: string, zIndex: number) {
    el.style.position = 'sticky';
    el.style.top = top;
    el.style.zIndex = String(zIndex);
}

function resolvePreviousElementSibling(el: HTMLElement): HTMLElement | null {
    if (el.previousElementSibling != null) {
        return el.previousElementSibling as HTMLElement;
    } else if (el.parentElement != null) {
        return resolvePreviousElementSibling(el.parentElement as HTMLElement);
    } else {
        return null;
    }
}

function unpackArguments(args: RawArguments): UnpackedArguments {
    if (args == null) {
        return {
            offset: 0,
            disabled: false,
            zIndex: 1,
        };
    } else if (typeof args === 'number') {
        return {
            offset: args,
            disabled: false,
            zIndex: 1,
        };
    } else {
        return {
            offset: args.offset ?? 0,
            disabled: args.disabled ?? false,
            zIndex: args.zIndex ?? 1,
        };
    }
}

function resolvePositionTop(topCss: string, offset: number): string {
    const topCssPx = parseInt(topCss.replace('px', ''), 10);
    return `${topCssPx + offset}px`;
}

function isElementHidden(el: HTMLElement): boolean {
    return el.offsetParent == null
        || window.getComputedStyle(el).display === 'none';
}

type RawArguments = undefined | number | {
    offset?: number;
    disabled?: boolean;
    zIndex?: number;
};

type UnpackedArguments = {
    offset: number;
    disabled: boolean;
    zIndex: number;
};
